import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import '../styles/fonts.css';
import '../styles/teams.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchData } from '../services/api';


function Leagues() {
  const { t } = useTranslation();
  usePageTitle('Leagues');
  useCanonicalTag();
  useMetaTags({
    title: 'Leagues | Celeritas',
    description: 'Browse iRacing leagues on Celeritas. Find and join sim racing leagues from the community.',
    url: 'https://celeritas.live/leagues',
  });
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [letterFilter, setLetterFilter] = useState('Todos');

  useEffect(() => {
    setLoading(true);
    fetchData(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/leagues/`)
      .then(data => {
        setLeagues(data);
        setError(null);
      })
      .catch(err => {
        console.error('Erro ao buscar ligas:', err);
        setError(t('leagues.loadError'));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="TeamsPage">
        <Header />
        <main>
          <section className="center-col teams-archive">
            <div className="box-header"><h3>{t('leagues.title')}</h3></div>
            <p>{t('leagues.loading')}</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="TeamsPage">
        <Header />
        <main>
          <section className="center-col teams-archive">
            <div className="box-header"><h3>{t('leagues.title')}</h3></div>
            <p className="error-message" style={{ color: 'red' }}>{error}</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const filtered = leagues.filter(l => {
    const matchesSearch = l.name.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (letterFilter === 'Todos') return true;
    const first = l.name[0].toUpperCase();
    if (letterFilter === '#') return !/[A-Z]/.test(first);
    return first === letterFilter;
  });

  return (
    <div className="TeamsPage">
      <Header />

      <main>
        <div style={{ maxWidth: '1600px', margin: '20px auto', padding: '0 16px' }}>
          <section className="teams-archive">
            <div className="tools-section">
              <nav className="alphabet-nav">
                {['Todos', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')].map(letter => (
                  <button
                    key={letter}
                    className={`letter-btn${letterFilter === letter ? ' active' : ''}`}
                    onClick={() => setLetterFilter(letter)}
                  >
                    {letter === 'Todos' ? t('common.all') : letter}
                  </button>
                ))}
              </nav>

              <div className="search-box">
                <input
                  type="text"
                  id="team-search"
                  placeholder={t('leagues.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <i className="fas fa-search search-icon"></i>
              </div>
            </div>

            <div className="teams-grid">
              <article className="team-card" style={{ border: '2px dashed #ff4747', background: 'transparent' }}>
                <div className="team-logo-bg" style={{ background: '#1b2127' }}>
                  <span style={{ fontSize: '32px', color: '#ff4747', lineHeight: 1 }}>+</span>
                </div>
                <div className="team-info-main">
                  <h4><Link to="/add-league" style={{ color: '#ff4747' }}>{t('common.addMyLeague')}</Link></h4>
                  <div className="team-stats-mini">
                    <span>{t('leagues.registerFree')}</span>
                  </div>
                </div>
                <Link to="/add-league" className="btn-profile" style={{ backgroundColor: '#ff4747' }}>{t('leagues.createLeague')}</Link>
              </article>

              {filtered.map(league => (
                <article className="team-card" key={league.league_id}>
                  <div
                    className="team-logo-bg"
                    style={{
                      backgroundImage: [league.small_logo, league.logo, '/placeholder-logo.png']
                        .filter(Boolean)
                        .map(u => `url("${u}")`)
                        .join(', '),
                    }}
                    aria-label={league.name}
                  />
                  <div className="team-info-main">
                    <h4>
                      <Link to={`/leagues/${league.league_id}`}>{league.name}</Link>
                    </h4>
                    {league.has_password && (
                      <span title={t('nav.verifiedTooltip')} style={{
                        display: 'inline-block', fontSize: '0.7rem', fontWeight: 600,
                        background: '#1a3d2b', color: '#4caf50',
                        border: '1px solid #2d6b3a', borderRadius: 4,
                        padding: '1px 7px', cursor: 'default',
                        marginBottom: 6, whiteSpace: 'nowrap',
                      }}>✓ {t('nav.verifiedBadge')}</span>
                    )}
                    <div className="team-stats-mini">
                      <span><i className="fas fa-calendar-alt"></i> {league.championship_count || 0} {t('leagues.championships')}</span>
                      <span><i className="fas fa-id-badge"></i> {t('leagues.id')} {league.league_id}</span>
                    </div>
                  </div>
                  <Link to={`/leagues/${league.league_id}`} className="btn-profile">{t('common.details')}</Link>
                </article>
              ))}

              {filtered.length === 0 && (searchQuery || letterFilter !== 'Todos') && (
                <p style={{ color: '#9ba3ab', gridColumn: '1 / -1' }}>{t('leagues.notFound')}</p>
              )}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Leagues;
